/** Origen público del sitio (debe coincidir con package.json "homepage" y canonical). */
export const SITE_ORIGIN = "https://www.gekkonect.com";

export const OG_IMAGE = `${SITE_ORIGIN}/img/webp/logo-gekkonect-last-round.webp`;

/** NAP consistente en schema, pie y contacto */
export const BUSINESS = {
  name: "Gekkonect",
  street: "Ciénega 6319-1, La Hacienda",
  locality: "Puebla",
  region: "PUE",
  postalCode: "72570",
  country: "MX",
  phones: ["+52 222 293 22 56", "+52 222 264 17 52"],
  telHref: ["tel:+522222932256", "tel:+522222641752"],
  geo: { lat: 19.00002773628825, lng: -98.19210632294777 },
};

/** Home — alineado con public/index.html tras build */
export const DEFAULT_DESCRIPTION =
  "Diseño y operación de software a la medida, integraciones, SaaS, cloud AWS y agentes de IA para escalar con control. Equipo en Puebla, proyectos en todo México.";

export const DEFAULT_TITLE =
  "Software empresarial, cloud AWS y agentes de IA | Gekkonect México";

export const PAGE_SEO = {
  about: {
    title: "Quiénes somos · Software empresarial, cloud e IA | Gekkonect",
    description:
      "Equipo senior en transformación digital: software a medida, arquitectura cloud (AWS), integraciones, SaaS y agentes de IA para procesos críticos. Puebla · México.",
  },
  service: {
    title:
      "Servicios · Desarrollo a medida, AWS, APIs, integraciones e IA | Gekkonect",
    description:
      "Desde descubrimiento hasta producción: aplicaciones web, software a medida, UI/UX, APIs, microservicios, integraciones ERP/CRM, movilidad, AWS, IA y automatización. México.",
  },
  gekkoAi: {
    title: "Gekko-AI · Agentes de IA y automatización empresarial | Gekkonect",
    description:
      "Plataforma AI SaaS para desplegar agentes con integraciones API, flujos y gobernanza: atención, ventas, operaciones y soporte. Demo con ingeniería en México.",
  },
  contact: {
    title: "Contacto · Software, cloud AWS y Gekko-AI | Gekkonect Puebla",
    description:
      "Agenda consultoría para software a medida, automatización, cloud AWS y Gekko-AI. Oficina: Ciénega 6319-1, La Hacienda, Puebla. Respuesta prioritaria a proyectos enterprise.",
  },
};

/**
 * BreadcrumbList para rutas internas.
 */
export function breadcrumbJsonLd(segment, path) {
  const trail = path.startsWith("/") ? path : `/${path}`;
  return {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Inicio",
        item: `${SITE_ORIGIN}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: segment,
        item: `${SITE_ORIGIN}${trail}`,
      },
    ],
  };
}

function faqMainEntity(items) {
  return items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  }));
}

/** Nodo FAQPage para @graph */
export function faqPageJsonLd(items, pageUrl) {
  return {
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    url: pageUrl,
    mainEntity: faqMainEntity(items),
  };
}

export function combineJsonLdGraph(...nodes) {
  const flat = nodes.flat().filter(Boolean);
  return {
    "@context": "https://schema.org",
    "@graph": flat,
  };
}

export const HOME_FAQ_ITEMS = [
  {
    question: "¿Qué diferencia a Gekkonect de una agencia de desarrollo genérica?",
    answer:
      "Actuamos como partner de plataforma: arquitectura, implementación, integraciones (ERP, CRM, APIs), cloud AWS, seguridad y evolución continua; no solo entrega puntual de código. Base en Puebla con alcance nacional.",
  },
  {
    question: "¿Atienden proyectos en todo México?",
    answer:
      "Sí. El equipo opera con metodologías remotas y presenciales según la fase del proyecto, con estándares enterprise en comunicación, seguridad y documentación.",
  },
  {
    question: "¿Cómo combinan inteligencia artificial con sistemas existentes?",
    answer:
      "Mediante agentes y flujos (Gekko-AI), integraciones API y gobernanza: la IA puede operar sobre tus CRM, mesas de ayuda, ERP o endpoints propios sin reemplazar todo el stack de un golpe.",
  },
  {
    question: "¿Qué incluye una primera conversación comercial?",
    answer:
      "Aclaramos objetivos, volumetría, riesgos y tecnología actual; proponemos un plan por fases con hitos y estimación de esfuerzo. Puedes iniciar por el formulario de contacto o solicitar demo de Gekko-AI.",
  },
];

export const ABOUT_FAQ_ITEMS = [
  {
    question: "¿Qué experiencia tienen en producto y operaciones enterprise?",
    answer:
      "Arquitectura, integraciones y entrega continua con foco en sistemas críticos: APIs, microservicios, AWS, seguridad y equipos mixtos negocio–tecnología. Documentamos decisiones y dejamos código mantenible.",
  },
  {
    question: "¿Trabajan con empresas fuera de Puebla?",
    answer:
      "Sí, de forma recurrente en todo México y proyectos con equipos distribuidos. La oficina en Puebla ancla operaciones y reuniones presenciales cuando aportan valor.",
  },
  {
    question: "¿Cómo se relacionan con Gekko-AI?",
    answer:
      "Gekko-AI es nuestra línea de producto SaaS para agentes de IA empresariales; el mismo estándar de ingeniería aplica a desarrollos a medida y a la plataforma.",
  },
];

export const SERVICE_FAQ_ITEMS = [
  {
    question: "¿Desarrollan software a medida para empresas reguladas o con alta carga?",
    answer:
      "Sí. Diseñamos escalabilidad, observabilidad, control de acceso y pruebas según criticidad. La arquitectura (APIs, microservicios, AWS) se adapta a requisitos de rendimiento y cumplimiento.",
  },
  {
    question: "¿Integran con SAP, Salesforce, Odoo u otros ERP/CRM?",
    answer:
      "Sí, mediante APIs, colas, ETL o conectores según madurez del sistema. Priorizamos contratos estables, idempotencia y monitoreo para que las integraciones sean mantenibles.",
  },
  {
    question: "¿Ofrecen consultoría cloud y migración a AWS?",
    answer:
      "Sí: diseño de landing zone, costos, seguridad, migraciones y operación. Acompañamos desde la evaluación hasta producción y optimización continua.",
  },
  {
    question: "¿Qué es Gekko-AI y cómo encaja con mis servicios actuales?",
    answer:
      "Gekko-AI es nuestra plataforma SaaS para agentes de IA empresariales: automatización de ventas, soporte y operaciones con integraciones y políticas de acceso. Se complementa con desarrollo a medida cuando hace falta lógica custom.",
  },
  {
    question: "¿Cuál es el modelo de entrega: proyecto fijo o equipo dedicado?",
    answer:
      "Ambos según madurez del roadmap. Fases bien acotadas para MVPs y expansión por releases, o dedicación continua para productos y plataformas en evolución.",
  },
];

export const CONTACT_FAQ_ITEMS = [
  {
    question: "¿En cuánto tiempo recibo respuesta tras enviar el formulario?",
    answer:
      "Proyectos B2B enterprise suelen recibir respuesta en horario hábil dentro de uno o dos días, con prioridad si incluyes volumetría, sistemas involucrados y plazos en el mensaje.",
  },
  {
    question: "¿Qué información conviene incluir en el primer contacto?",
    answer:
      "Contexto del negocio, procesos a digitalizar o automatizar, sistemas actuales (ERP/CRM), integraciones deseadas, estimación de usuarios o transacciones, y si evalúan Gekko-AI o desarrollo a medida.",
  },
  {
    question: "¿Puedo visitar las oficinas en Puebla?",
    answer:
      "Estamos en proceso de reubicación: al agendar tu cita, solicita la dirección actualizada. También coordinamos reuniones remotas sin problema.",
  },
];

/** Graph compartido home: WebSite + Organization (ProfessionalService) */
export function homeJsonLd() {
  const org = {
    "@type": ["Organization", "ProfessionalService"],
    "@id": `${SITE_ORIGIN}/#organization`,
    name: BUSINESS.name,
    url: `${SITE_ORIGIN}/`,
    description:
      "Partner de transformación digital: software empresarial a medida, cloud AWS, integraciones, SaaS y agentes de inteligencia artificial (Gekko-AI). Puebla, México.",
    logo: {
      "@type": "ImageObject",
      url: OG_IMAGE,
    },
    image: OG_IMAGE,
    address: {
      "@type": "PostalAddress",
      streetAddress: BUSINESS.street,
      addressLocality: BUSINESS.locality,
      addressRegion: BUSINESS.region,
      postalCode: BUSINESS.postalCode,
      addressCountry: BUSINESS.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.geo.lat,
      longitude: BUSINESS.geo.lng,
    },
    telephone: BUSINESS.telHref[0].replace("tel:", ""),
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: BUSINESS.telHref[0].replace("tel:", ""),
        contactType: "sales",
        areaServed: "MX",
        availableLanguage: ["Spanish", "es"],
      },
    ],
    areaServed: [
      { "@type": "Country", name: "México" },
      { "@type": "City", name: "Puebla" },
    ],
    sameAs: [
      "https://www.linkedin.com/company/gekkonect",
      "https://x.com/gekkonectAI",
    ],
  };

  const website = {
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: `${SITE_ORIGIN}/`,
    name: BUSINESS.name,
    inLanguage: "es-MX",
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  };

  return combineJsonLdGraph(
    website,
    org,
    faqPageJsonLd(HOME_FAQ_ITEMS, `${SITE_ORIGIN}/`)
  );
}

export function aboutPageJsonLd() {
  return combineJsonLdGraph(
    breadcrumbJsonLd("Nosotros", "/about"),
    faqPageJsonLd(ABOUT_FAQ_ITEMS, `${SITE_ORIGIN}/about`)
  );
}

export function servicePageJsonLd() {
  return combineJsonLdGraph(
    breadcrumbJsonLd("Servicios", "/service"),
    faqPageJsonLd(SERVICE_FAQ_ITEMS, `${SITE_ORIGIN}/service`)
  );
}

export function contactPageJsonLd() {
  return combineJsonLdGraph(
    breadcrumbJsonLd("Contacto", "/contact"),
    faqPageJsonLd(CONTACT_FAQ_ITEMS, `${SITE_ORIGIN}/contact`)
  );
}

export const GEKKO_AI_FAQ_ITEMS = [
  {
    question: "¿Gekko-AI reemplaza a mi CRM o help desk?",
    answer:
      "No necesariamente. Los agentes se integran con sistemas existentes (APIs, webhooks) para ejecutar flujos, consultar datos y escalar a humanos cuando la política lo requiere.",
  },
  {
    question: "¿Qué nivel de gobernanza ofrece para empresas?",
    answer:
      "Roles, permisos, límites de herramientas, trazas y versionado de conocimiento alineado con IT: pensado para producción, no para experimentos sin control.",
  },
  {
    question: "¿Cuánto tarda un piloto típico?",
    answer:
      "Depende del caso y de las integraciones; una primera ola operativa suele priorizar un flujo de alto impacto (p. ej. soporte o calificación de leads) antes de escalar el catálogo de agentes.",
  },
  {
    question: "¿Dónde solicito una demostración?",
    answer:
      "Desde el formulario de contacto en gekkonect.com/contact indicando que interesa Gekko-AI; el equipo comercial e ingeniería coordina la sesión y los siguientes pasos.",
  },
];

export function gekkoAiPageJsonLd(descriptionText) {
  const software = {
    "@type": "SoftwareApplication",
    name: "Gekko-AI",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: descriptionText,
    url: `${SITE_ORIGIN}/gekko-ai`,
    provider: {
      "@type": "Organization",
      "@id": `${SITE_ORIGIN}/#organization`,
      name: "Gekkonect",
      url: SITE_ORIGIN,
    },
  };
  return combineJsonLdGraph(
    breadcrumbJsonLd("Gekko-AI", "/gekko-ai"),
    software,
    faqPageJsonLd(GEKKO_AI_FAQ_ITEMS, `${SITE_ORIGIN}/gekko-ai`)
  );
}
